import React, { useState, useEffect, useCallback } from 'react';
import { baseUrl, assetFullLabel } from '../../shared';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import OpacityIcon from '@mui/icons-material/Opacity';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkshopJobModal from '../../components/workshop/WorkshopJobModal';
import WorkshopJobDetailModal from '../../components/workshop/WorkshopJobDetailModal';
import { StatusChip } from '../../components/ui';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

export const STATUS_COLOR = {
  IN_TRANSIT: '#9c27b0',
  TEARDOWN: '#ed6c02',
  TEARDOWN_COMPLETE: '#0288d1',
  QUOTE_SENT: '#f9a825',
  IN_PROGRESS: '#1976d2',
  COMPLETE: '#2e7d32',
  CANCELLED: '#757575',
};

export const STATUS_LABELS = {
  IN_TRANSIT: 'In Transit',
  TEARDOWN: 'Teardown',
  TEARDOWN_COMPLETE: 'Teardown Complete',
  QUOTE_SENT: 'Quote Sent',
  IN_PROGRESS: 'In Progress',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
};

export const APPROVAL_COLOR = {
  PENDING: '#ed6c02',
  APPROVED: '#2e7d32',
  NOT_APPROVED: '#d32f2f',
};

export const APPROVAL_LABELS = {
  PENDING: 'Pending Approval',
  APPROVED: 'Approved',
  NOT_APPROVED: 'Not Approved',
};

export const JOB_TYPE_LABELS = {
  REPAIR: 'Repair',
  SUPPLY: 'Supply',
  CALIBRATION: 'Calibration',
  SITE_VISIT: 'Site Visit',
};

const OPEN_STATUSES = ['IN_TRANSIT', 'TEARDOWN', 'TEARDOWN_COMPLETE', 'QUOTE_SENT', 'IN_PROGRESS'];

function JobCard({ job, onClick }) {
  const isOverdue = job.quoteActionDue && dayjs(job.quoteActionDue).isBefore(dayjs(), 'day')
    && job.status && job.status.toUpperCase() !== 'COMPLETE'
    && job.status.toUpperCase() !== 'CANCELLED';

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.25,
        borderColor: isOverdue ? 'error.main' : 'var(--border)',
        borderLeft: `4px solid ${isOverdue ? 'var(--fail)' : (STATUS_COLOR[job.status] || 'var(--neutral)')}`,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <CardActionArea onClick={() => onClick(job)}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {job.customer || '—'}{assetFullLabel(job) ? ` · ${assetFullLabel(job)}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={JOB_TYPE_LABELS[job.jobType] || job.jobType}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10 }}
              />
              <StatusChip domain="job" value={job.status} label={STATUS_LABELS[job.status] || job.status} />
              {job.approval && job.approval.toUpperCase() !== 'PENDING' && (
                <StatusChip domain="approval" value={job.approval} label={APPROVAL_LABELS[job.approval] || job.approval} />
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {job.item ? `${job.item}` : ''}
            {job.manufacturer ? ` · ${job.manufacturer}` : ''}
            {job.model ? ` ${job.model}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            {job.customerPO && (
              <Typography variant="caption" color="text.secondary">PO: {job.customerPO}</Typography>
            )}
            {job.quoteNo && (
              <Typography variant="caption" color="text.secondary">Quote: {job.quoteNo}</Typography>
            )}
            {job.jobAssignedTo && (
              <Typography variant="caption" color="text.secondary">Assigned: {job.jobAssignedTo}</Typography>
            )}
            {job.createdAt && (
              <Typography variant="caption" color="text.secondary">
                Created: {dayjs(job.createdAt).format('DD/MM/YYYY')}
              </Typography>
            )}
            {isOverdue && (
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                Quote overdue
              </Typography>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function Workshop() {
  const canManage = localStorage.getItem('canManageWorkshop') === '1';
  const isAdmin = localStorage.getItem('canRegisterUser') === '1';
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listTab, setListTab] = useState(0); // 0=open, 1=complete/cancelled
  const [filterStatus, setFilterStatus] = useState('');
  const [filterJobType, setFilterJobType] = useState('');
  const [filterApproval, setFilterApproval] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadJobs = useCallback(() => {
    setLoading(true);
    fetch(baseUrl + 'workshop/jobs', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setJobs(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadJobs();
    fetch(baseUrl + 'company/list', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setCompanies(Array.isArray(d) ? d : []));
  }, [loadJobs]);

  const openDetail = async (job) => {
    const res = await fetch(baseUrl + 'workshop/jobs/' + job.id, { headers: authHeaders() });
    const data = await res.json();
    setSelectedJob(data);
    setDetailOpen(true);
  };

  const openJobs = jobs.filter(j => OPEN_STATUSES.includes(j.status && j.status.toUpperCase()));
  const closedJobs = jobs.filter(j => !OPEN_STATUSES.includes(j.status && j.status.toUpperCase()));

  const applyFilters = (list) => list.filter(j => {
    if (filterStatus && j.status !== filterStatus) return false;
    if (filterJobType && j.jobType !== filterJobType) return false;
    if (filterApproval && j.approval !== filterApproval) return false;
    if (filterCustomer && j.customer !== filterCustomer) return false;
    return true;
  });

  const displayedJobs = applyFilters(listTab === 0 ? openJobs : closedJobs);
  const hasFilters = filterStatus || filterJobType || filterApproval || filterCustomer;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Workshop</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New Job
            </Button>
          )}
          {canManage && (
            <Button variant="outlined" size="small" onClick={() => navigate('/workshop/non-approvals')}>
              Pending Approval
            </Button>
          )}
          {canManage && (
            <Tooltip title="Cylinder Reference">
              <IconButton onClick={() => navigate('/workshop/cylinder-reference')}>
                <OpacityIcon />
              </IconButton>
            </Tooltip>
          )}
          {isAdmin && (
            <Tooltip title="Hours Summary">
              <IconButton onClick={() => navigate('/workshop/hours')}>
                <AccessTimeIcon />
              </IconButton>
            </Tooltip>
          )}
          {canManage && (
            <Tooltip title="Workshop Stats">
              <IconButton onClick={() => navigate('/workshop/stats')}>
                <BarChartIcon />
              </IconButton>
            </Tooltip>
          )}
          {canManage && (
            <Tooltip title="Workshop Settings">
              <IconButton onClick={() => navigate('/workshop/settings')}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Customer</InputLabel>
          <Select value={filterCustomer} label="Filter by Customer" onChange={e => setFilterCustomer(e.target.value)}>
            <MenuItem value=""><em>All Customers</em></MenuItem>
            {companies.map(c => (
              <MenuItem key={c.company} value={c.company}>{c.company}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select value={filterJobType} label="Filter by Type" onChange={e => setFilterJobType(e.target.value)}>
            <MenuItem value=""><em>All Types</em></MenuItem>
            {Object.entries(JOB_TYPE_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {listTab === 0 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select value={filterStatus} label="Filter by Status" onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value=""><em>All Statuses</em></MenuItem>
              {OPEN_STATUSES.map(s => (
                <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter by Approval</InputLabel>
          <Select value={filterApproval} label="Filter by Approval" onChange={e => setFilterApproval(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {Object.entries(APPROVAL_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasFilters && (
          <Button size="small" variant="text" onClick={() => {
            setFilterStatus(''); setFilterJobType(''); setFilterApproval(''); setFilterCustomer('');
          }}>
            Clear filters
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Tabs value={listTab} onChange={(_, v) => { setListTab(v); setFilterStatus(''); }}>
              <Tab label={`Open (${applyFilters(openJobs).length})`} />
              <Tab label="Closed" />
            </Tabs>
            {hasFilters && (
              <Typography variant="caption" color="text.secondary">
                Showing {displayedJobs.length} of {listTab === 0 ? openJobs.length : closedJobs.length} jobs
              </Typography>
            )}
          </Box>

          {displayedJobs.length === 0
            ? <Typography color="text.secondary">No jobs found.</Typography>
            : displayedJobs.map(j => <JobCard key={j.id} job={j} onClick={openDetail} />)
          }
        </Box>
      )}

      <WorkshopJobModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        job={null}
        companies={companies}
        onSaved={() => { loadJobs(); setCreateOpen(false); }}
      />

      <WorkshopJobDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        job={selectedJob}
        companies={companies}
        canManage={canManage}
        onSaved={() => { loadJobs(); setDetailOpen(false); }}
        onUpdated={(updated) => { setSelectedJob(updated); loadJobs(); }}
        onDeleted={() => { loadJobs(); setDetailOpen(false); }}
      />
    </Box>
  );
}
